import React from 'react';
import provideDataHook, {
  DataHookFunctionProps
} from '~/helpers/provideDataHook';
import Head from '~/App/shared/components/Head';
import NotFound from '~/App/shared/components/NotFound';
import Section from '~/App/shared/components/Layout/Section';
import ScrollTo from '~/App/shared/components/ScrollTo';
import StickyBlock from '~/App/shared/components/StickyBlockComponent';
import { getFacility } from '~/helpers/orchestration/facilities';
import useCanonicalUrl from '~/App/shared/hooks/useCanonicalUrl';
import { useLocation, useParams } from 'react-router';
import { useTranslation } from '~/Locale';
import { getTableOfContents } from '../../helpers';
import FacilityContent from '../../components/FacilityContent';
import styled from 'styled-components';
import { mq, Spinner, Subtitle } from '@kvdbil/components';
import { RouteComponentProps } from '~/App/shared/interfaces/routes';
import { useCmsData } from '~/App/shared/hooks/useCmsData';
import { FacilityDocument, FacilityQuery } from '~/config/generated/graphql';
import { FacilityData } from '~/App/shared/interfaces/store/CmsData';
import StatusPageContainer from '~/App/shared/components/StatusPageContainer';
import { FacilityWithAvailabilities } from '~/App/shared/interfaces/Facility';

export const Container = styled.div<{ $isPageMenu?: boolean }>`
  display: flex;
  padding: 1.5rem 0;

  ${({ $isPageMenu = true }) =>
    !$isPageMenu &&
    `
    margin: 0 auto;
    max-width: 41.25rem;
  `}

  &:after,
  &:before {
    content: '';
    display: table;
  }

  &:after {
    clear: both;
  }

  ${mq('tablet')} {
    padding: 2.5rem;
  }
`;

export const ContentWrapper = styled.div`
  display: flex;
`;

export const Side = styled.div`
  display: none;

  ${mq('tablet')} {
    display: inline-block;
    vertical-align: top;
    width: 250px;
    padding-right: 2rem;
  }

  ${mq('laptop')} {
    width: 360px;
  }
`;

export const Content = styled.div<{ $isPageMenu?: boolean }>`
  ${({ $isPageMenu = true }) =>
    !$isPageMenu &&
    `
      width: 100%;
    `}

  ${({ $isPageMenu = true }) =>
    $isPageMenu &&
    `
    ${mq('tablet')} {
      width: calc(100% - 250px);
    }

    ${mq('laptop')} {
      width: calc(100% - 360px);
    }
  `}
`;

/* Value Explained: max-width: <pixels> === _page.scss &--slim */
export const FacilitySection = styled.section`
  max-width: 660px;
  padding: 0.5rem;
  margin: 0.5rem auto;

  .auctionFacility__map {
    margin: 0.5rem;
  }

  ${mq('tablet')} {
    padding: 0rem 3rem;
    margin: -1rem auto 2rem;
  }
`;

export const PageHeading = styled(Subtitle)`
  margin-bottom: 1.5rem;
  font-weight: ${({ theme }) => theme.typography.fontAlternativeBoldWeight};

  ${mq('tablet')} {
    margin-bottom: 2.5rem;
  }
`;

interface Props extends RouteComponentProps {
  data: {
    facility: FacilityWithAvailabilities;
    title: string;
  };
}

export const Facility = ({ data, staticContext }: Props) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const canonicalUrl = useCanonicalUrl(pathname);
  const { slug } = useParams<{ slug: string }>();

  const { data: cmsData, fetching } = useCmsData<FacilityQuery, FacilityData>({
    storeKey: 'facilityPage',
    query: FacilityDocument,
    options: { variables: { slug } },
    storeNestedKey: `facilityId-${slug}`
  });

  if (fetching) {
    return (
      <StatusPageContainer $minHeight={40} $unit="vh">
        <Spinner size="large" color="neutral" />
      </StatusPageContainer>
    );
  }

  if (!data) {
    return (
      <Section>
        <NotFound />
      </Section>
    );
  }

  const { facility } = data;

  if (!facility || !cmsData) {
    if (staticContext) {
      staticContext.statusCode = 404;
    }

    return (
      <Section>
        <NotFound />
      </Section>
    );
  }

  if (staticContext) {
    staticContext.statusCode = 200;
  }

  const image =
    cmsData?.image?.responsiveImage?.src ?? '//placehold.it/900x520';

  const tableOfContents = getTableOfContents(cmsData, t);

  const description = `
    ${cmsData?.streetAddress} ${cmsData?.zipCode} ${cmsData?.city}
    `;

  const meta = [
    { name: 'description', content: description },
    { name: 'og:description', content: description },
    { name: 'og:title', content: cmsData?.title ?? '' },
    { name: 'og:image', content: image },
    { name: 'og:type', content: t('PAGE_META.OG_TYPE') }
  ];
  cmsData?.image?.responsiveImage?.alt &&
    meta.push({
      name: 'og:image;alt',
      content: cmsData?.image?.responsiveImage?.alt
    });

  return (
    <div>
      <Head
        title={`${t('PAGE_TITLES.FACILITY')} ${cmsData?.title}`}
        meta={meta}
        link={[{ rel: 'canonical', href: canonicalUrl }]}
      />

      <Section spacing={0}>
        <Container>
          <Side>
            <StickyBlock topOffset={20} bottomOffset={100}>
              <div>
                <PageHeading as="h1">{cmsData?.title}</PageHeading>
              </div>
              <ScrollTo items={tableOfContents} offset={-150} />
            </StickyBlock>
          </Side>
          <Content>
            <FacilityContent
              cmsData={cmsData}
              facility={facility}
              tableOfContents={tableOfContents}
            />
          </Content>
        </Container>
      </Section>
    </div>
  );
};

const dataHook = async ({ match }: DataHookFunctionProps<{ slug: string }>) => {
  const { params } = match;
  try {
    const facility = await getFacility({ slug: params.slug });
    return { data: { facility, title: facility.name } };
  } catch (err: unknown) {
    return {};
  }
};

export default provideDataHook('Facility', dataHook)(Facility);
